import React, { useState } from "react";
import { Accordion, Badge, Card, Table } from "react-bootstrap";
import { FormattedMessage, FormattedNumber, useIntl } from "react-intl";
import styled from "styled-components";
import { StyledReactTooltip } from "../../../shared/actions/tableActions";
import { Chevron } from "../../../shared/crud/list";
import {
  DateRange,
  ExperimentResults,
  MetricComparisonEntity,
  MetricRange,
} from "../domain/experiment.entity";
import { ExperimentRepository } from "../domain/interfaces/ExperimentRepository";
import { useExperimentResults } from "../application/hooks/useExperimentResults";
import { messages } from "../experiment.messages";

export interface AllMetricsTableProps {
  propertyTicker: string;
  experimentId: string;
  repository: ExperimentRepository;
  range: MetricRange; // read-only prop from ExperimentResultsPage (US-017, Business Rule 11)
  custom: DateRange | undefined; // read-only prop, undefined unless range === "CUSTOM"
  /** Rendered at the right of the table's header row — the page passes its export links here.
   *  Taken as a node rather than built in place so this component keeps knowing nothing about the
   *  export stack's HTTP client, file downloads or SweetAlert. */
  headerActions?: React.ReactNode;
}

// Business Rule 1: the backend's `table` array is in raw proto-field order — this is the
// front-end-only, RM-relevance display order this component re-sorts into.
const METRIC_DISPLAY_ORDER = [
  "visits",
  "revenue_per_visit",
  "revenue_total",
  "bookings",
  "conversion_rate",
  "adr",
  "avg_booking_value",
  "abandonment_rate",
] as const;

// Business Rule 3: the *only* hardcoded per-metric lookup this component needs — resolves both
// the display name (`messages.metrics[key]`) and the tooltip definition
// (`messages.metrics.desc[key]`). Contrast with `higherIsBetter`/`diffUnit`, which are read
// directly off each wire row (Plan vs. Actual point 2).
type MetricMessageKey = Exclude<keyof typeof messages.metrics, "desc">;

const METRIC_MESSAGE_KEY: Record<string, MetricMessageKey> = {
  visits: "visits",
  bookings: "bookings",
  conversion_rate: "conversionRate",
  revenue_total: "revenueTotal",
  revenue_per_visit: "revenuePerVisit",
  adr: "adr",
  avg_booking_value: "avgBookingValue",
  abandonment_rate: "abandonmentRate",
};

// Business Rule 5: three type classes drive number formatting. Counts and money metrics are
// listed explicitly; every other (known or future/unknown) metric falls through to the
// plain-decimal percent-shaped branch, which is the safest default for an unrecognized metric.
/**
 * The confidence at which the backend declares a winner. A per-row medal is gated on the same
 * threshold: `MetricComparison.winner` is pure arithmetic with no significance gate of its own, so
 * 1 booking against 2 would otherwise read as "Ganador B" (plan §5.8).
 */
const WINNER_CONFIDENCE_THRESHOLD = 0.95;

const COUNT_METRIC_KEYS = new Set(["visits", "bookings"]);
const MONEY_METRIC_KEYS = new Set([
  "revenue_total",
  "revenue_per_visit",
  "adr",
  "avg_booking_value",
]);

function getDisplayIndex(metricKey: string): number {
  return METRIC_DISPLAY_ORDER.indexOf(metricKey as typeof METRIC_DISPLAY_ORDER[number]);
}

// Business Rule 1 (edge case): a `metricKey` absent from `METRIC_DISPLAY_ORDER` sorts to `-1`
// (lands first) — a defensive `console.warn` records this rather than silently mis-ordering.
function sortRowsForDisplay(table: MetricComparisonEntity[]): MetricComparisonEntity[] {
  const unknownKeys = new Set(
    table.filter(row => getDisplayIndex(row.metricKey) === -1).map(row => row.metricKey)
  );
  unknownKeys.forEach(key =>
    // eslint-disable-next-line no-console
    console.warn(
      `AllMetricsTable: metricKey "${key}" is not in METRIC_DISPLAY_ORDER — rendering it with a raw fallback label instead of dropping it.`
    )
  );
  return [...table].sort((a, b) => getDisplayIndex(a.metricKey) - getDisplayIndex(b.metricKey));
}

const ScenarioBadge = styled(Badge)<{ $scenario: "A" | "B" }>`
  background-color: ${({ theme, $scenario }) =>
    $scenario === "A" ? theme.orangeBackground : theme.darkBlueBackground};
  color: #fff;
`;

const TABLE_HEADER_BACKGROUND = "#f8f9fa";

const StyledThead = styled.thead`
  && th {
    background-color: ${TABLE_HEADER_BACKGROUND};
    border-bottom: none;
  }
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
`;

/** Only this half of the header row toggles the accordion — the export links beside it must not. */
const HeaderToggle = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  flex: 1 1 auto;
  min-width: 0;
  cursor: pointer;
`;

const Footnote = styled.div`
  padding: 0.5rem 0.75rem;
  border-top: 1px solid ${({ theme }) => theme.buttonLightBorder};
  font-size: 0.72rem;
  color: ${({ theme }) => theme.regularTextColor};
`;

function MetricNameCell({ metricKey }: { metricKey: string }) {
  const messageKey = METRIC_MESSAGE_KEY[metricKey];
  if (!messageKey) {
    // Unrecognized metricKey: no translation available, render the raw wire key (Error Handling
    // table / AC9) rather than dropping the row.
    return <span data-testid={`metric-name-${metricKey}`}>{metricKey}</span>;
  }
  const tooltipId = `all-metrics-table-tooltip-${metricKey}`;
  return (
    <span data-testid={`metric-name-${metricKey}`}>
      <abbr
        data-tip="React-tooltip"
        data-for={tooltipId}
        style={{ textDecoration: "underline dotted", cursor: "help" }}>
        <FormattedMessage {...messages.metrics[messageKey]} />
      </abbr>
      <StyledReactTooltip id={tooltipId} effect="solid">
        <FormattedMessage {...messages.metrics.desc[messageKey]} />
      </StyledReactTooltip>
    </span>
  );
}

function WinnerCell({
  winner,
  significant,
}: {
  winner: MetricComparisonEntity["winner"];
  significant: boolean;
}) {
  if (winner === "" || !significant) return <>{"—"}</>;
  return <ScenarioBadge $scenario={winner}>{winner}</ScenarioBadge>;
}

/**
 * The response carries exactly one `confidence` — the conversion-rate z-test's. `MetricComparison`
 * has no per-metric confidence field (proto fields 1-7), so rather than repeat one metric's number
 * next to seven unrelated ones, it is shown only on the row it actually describes (design.md,
 * Decision 8).
 */
function ConfidenceCell({ metricKey, confidence }: { metricKey: string; confidence: number }) {
  if (metricKey !== "conversion_rate") return <>{"—"}</>;
  return (
    <>
      <FormattedNumber value={confidence * 100} maximumFractionDigits={0} />%
    </>
  );
}

/**
 * The visits row reports how traffic actually split. The configured split is fixed at 50/50 and
 * not surfaced anywhere in the results panel, so there is no "against" side to contrast it with —
 * the SRM alert is what speaks up when the two diverge.
 */
function ObservedSplitCell({ results }: { results: ExperimentResults }) {
  const intl = useIntl();
  const observedA = results.observedShareA * 100;
  // `toFixed` always writes an English decimal point; the figure beside it comes from
  // `intl.formatNumber`, so the row used to read "50.3 % / 49,7 %" in a Spanish session.
  const oneDecimal = (value: number) =>
    intl.formatNumber(value, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  return (
    <span data-testid="observed-split">
      <FormattedMessage
        {...messages.results.table.observedSplit}
        values={{
          observedA: oneDecimal(observedA),
          observedB: oneDecimal(100 - observedA),
        }}
      />
    </span>
  );
}

// Business Rule 5: money values are formatted per-scenario, using that scenario's own
// `ScenarioMetricsSchema.currency` — never a hardcoded currency code (Plan vs. Actual point 6).
function MetricValueCell({
  value,
  metricKey,
  currency,
}: {
  value: number;
  metricKey: string;
  currency: string;
}) {
  // Business Rule 8: defensive `Number.isFinite` guard on every numeric render.
  if (!Number.isFinite(value)) return <>{"—"}</>;

  if (COUNT_METRIC_KEYS.has(metricKey)) {
    return <FormattedNumber value={value} maximumFractionDigits={0} />;
  }

  if (MONEY_METRIC_KEYS.has(metricKey)) {
    // Business Rule 7: `currency` comes back as "" on a `partial` response
    // (`RawMetricsResult.empty()`'s convention), and `style: "currency"` throws on an empty code.
    // The plain number is rendered instead, exactly as the scenario cards already do.
    if (!currency) {
      return <FormattedNumber value={value} minimumFractionDigits={2} maximumFractionDigits={2} />;
    }
    return (
      <FormattedNumber
        value={value}
        style="currency"
        currency={currency}
        minimumFractionDigits={2}
        maximumFractionDigits={2}
      />
    );
  }

  // Percentage-shaped metrics (conversion_rate, abandonment_rate): the backend already scales
  // these by 100, so `style="percent"` would double-scale — plain decimal plus a hand-appended,
  // non-localized `%` instead (Business Rule 5).
  return (
    <>
      <FormattedNumber value={value} minimumFractionDigits={2} maximumFractionDigits={2} />%
    </>
  );
}

// Business Rule 6/7: direction-aware coloring keyed off `row.higherIsBetter`, never a blanket
// sign-based rule; `relativeDiff === null` always renders `—`, no color, no extra heuristic.
function RelativeDiffCell({ row }: { row: MetricComparisonEntity }) {
  const { relativeDiff, higherIsBetter } = row;

  if (relativeDiff === null || !Number.isFinite(relativeDiff)) {
    return <>{"—"}</>;
  }

  const isTie = relativeDiff === 0;
  const isImprovement = higherIsBetter ? relativeDiff > 0 : relativeDiff < 0;
  const colorClass = isTie ? undefined : isImprovement ? "text-success" : "text-danger";

  return (
    <span className={colorClass}>
      <FormattedNumber value={relativeDiff} minimumFractionDigits={2} maximumFractionDigits={2} />%
    </span>
  );
}

export const AllMetricsTable: React.FC<AllMetricsTableProps> = ({
  propertyTicker,
  experimentId,
  repository,
  range,
  custom,
  headerActions,
}) => {
  // Business Rule 11: this component owns no `range`/`custom` state of its own — it calls
  // `useExperimentResults` itself (US-017's Component Tree architecture note), sharing a single
  // network call with every other simultaneously-mounted results-page section via React Query's
  // identical-query-key de-duplication.
  const { data: results, isLoading, isError } = useExperimentResults(
    propertyTicker,
    experimentId,
    range,
    repository,
    custom
  );

  const [toggleClosed, setToggleClosed] = useState(false);

  return (
    <Accordion defaultActiveKey="0">
      <Card>
        <Card.Header style={{ backgroundColor: "white" }}>
          <HeaderRow>
            <Accordion.Toggle
              as={HeaderToggle}
              eventKey="0"
              onClick={() => setToggleClosed(prev => !prev)}>
              <h6 className="mb-0">
                <FormattedMessage {...messages.results.allMetrics} />
              </h6>
              <Chevron toggleClosed={toggleClosed} />
            </Accordion.Toggle>
            {headerActions && <div data-testid="all-metrics-actions">{headerActions}</div>}
          </HeaderRow>
        </Card.Header>
        <Accordion.Collapse eventKey="0">
          <Card.Body className="p-0">
            {isLoading && (
              <div className="p-3">
                <FormattedMessage {...messages.results.loading} />
              </div>
            )}

            {!isLoading && (isError || !results || !Array.isArray(results.table)) && (
              <div className="p-3">
                <FormattedMessage {...messages.results.dataUnavailable} />
              </div>
            )}

            {!isLoading && !isError && results && Array.isArray(results.table) && (
              <>
                {/* A partial fetch degrades individual metrics to 0 in the table below (still
                    rendered normally) rather than blocking the whole table — a page-level
                    console.warn diagnostic (ExperimentResults.tsx) already covers this, so no
                    redundant banner here on top of the already-visible degraded values. */}
                <Table hover responsive className="mb-0">
                  <StyledThead>
                    <tr>
                      <th>
                        <FormattedMessage {...messages.results.table.metric} />
                      </th>
                      <th>
                        <FormattedMessage
                          {...messages.results.table.scenarioA}
                          values={{ name: results.scenarioA.variantTicker }}
                        />
                      </th>
                      <th>
                        <FormattedMessage
                          {...messages.results.table.scenarioB}
                          values={{ name: results.scenarioB.variantTicker }}
                        />
                      </th>
                      <th>
                        <FormattedMessage {...messages.results.table.confidence} />
                      </th>
                      <th>
                        <FormattedMessage {...messages.results.table.winner} />
                      </th>
                      <th>
                        <FormattedMessage {...messages.results.table.relativeDiff} />
                      </th>
                    </tr>
                  </StyledThead>
                  <tbody>
                    {sortRowsForDisplay(results.table).map(row => {
                      // Business Rule 2: the Visits row never shows a winner or a difference,
                      // regardless of its own wire `winner`/`relativeDiff` values — a deliberate,
                      // unconditional front-end display suppression. It now reports the observed
                      // split instead, which is the question that row actually answers.
                      const isVisitsRow = row.metricKey === "visits";
                      const significant = results.confidence >= WINNER_CONFIDENCE_THRESHOLD;
                      return (
                        <tr key={row.metricKey}>
                          <td>
                            <MetricNameCell metricKey={row.metricKey} />
                          </td>
                          <td>
                            <MetricValueCell
                              value={row.valueA}
                              metricKey={row.metricKey}
                              currency={results.scenarioA.currency}
                            />
                          </td>
                          <td>
                            <MetricValueCell
                              value={row.valueB}
                              metricKey={row.metricKey}
                              currency={results.scenarioB.currency}
                            />
                          </td>
                          <td>
                            <ConfidenceCell
                              metricKey={row.metricKey}
                              confidence={results.confidence}
                            />
                          </td>
                          <td>
                            {isVisitsRow ? (
                              "—"
                            ) : (
                              <WinnerCell winner={row.winner} significant={significant} />
                            )}
                          </td>
                          <td>
                            {isVisitsRow ? (
                              <ObservedSplitCell results={results} />
                            ) : (
                              <RelativeDiffCell row={row} />
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
                {/* One line, so the Confianza column is not read as seven independent p-values. */}
                <Footnote data-testid="all-metrics-footnote">
                  <FormattedMessage {...messages.results.tableFootnote} />
                </Footnote>
              </>
            )}
          </Card.Body>
        </Accordion.Collapse>
      </Card>
    </Accordion>
  );
};
